// OWNER: Agent A — do not edit if you are not Agent A.
// Two-step email -> code login. Always shows the generic "OTP sent" copy,
// regardless of whether the account exists (PLAN.md §4/§6).
import { useState, useCallback, useEffect, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Button } from "@cloudflare/kumo/components/button";
import { Input } from "@cloudflare/kumo/components/input";
import { Banner } from "@cloudflare/kumo/components/banner";
import { Text } from "@cloudflare/kumo/components/text";
import { OTPField } from "@cloudflare/kumo/primitives/otp-field";
import { useAuth } from "@client/lib/auth";
import { ApiRequestError } from "@client/lib/api";

const RESEND_COOLDOWN_SECONDS = 60;
const OTP_SENT_MESSAGE = "If that account exists, an OTP has been sent to your email.";

type Step = "email" | "code";

export default function LoginPage() {
  const { requestOtp, verifyOtp } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const sendCode = useCallback(
    async (targetEmail: string) => {
      setError(null);
      setSubmitting(true);
      try {
        await requestOtp(targetEmail);
        setStep("code");
        setCode("");
        setCooldown(RESEND_COOLDOWN_SECONDS);
      } catch (err) {
        setError(err instanceof ApiRequestError ? err.apiError.message : "Something went wrong. Try again.");
      } finally {
        setSubmitting(false);
      }
    },
    [requestOtp],
  );

  const handleEmailSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || submitting) return;
    void sendCode(trimmed);
  };

  const submitCode = useCallback(
    async (value: string) => {
      if (value.length !== 6 || submitting) return;
      setError(null);
      setSubmitting(true);
      try {
        await verifyOtp(email.trim(), value);
        navigate(next, { replace: true });
      } catch {
        setError("Invalid or expired code");
        setCode("");
      } finally {
        setSubmitting(false);
      }
    },
    [email, navigate, next, submitting, verifyOtp],
  );

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-sm space-y-6">
        <Text variant="heading" size="lg">
          Sign in to Gringotts
        </Text>

        {step === "email" && (
          <form className="space-y-4" onSubmit={handleEmailSubmit}>
            <Input
              label="Email"
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
            {error && <Banner variant="error" description={error} />}
            <Button type="submit" variant="primary" loading={submitting} className="w-full">
              Send code
            </Button>
          </form>
        )}

        {step === "code" && (
          <div className="space-y-4">
            <Banner variant="default" description={OTP_SENT_MESSAGE} />

            <OTPField.Root
              length={6}
              value={code}
              autoComplete="one-time-code"
              disabled={submitting}
              onValueChange={(value) => {
                setCode(value);
                setError(null);
              }}
              onValueComplete={(value) => void submitCode(value)}
              className="flex justify-center gap-2"
            >
              {Array.from({ length: 6 }).map((_, i) => (
                <OTPField.Input
                  key={i}
                  className="h-12 w-10 rounded-md border border-kumo-line bg-kumo-control text-center text-lg text-kumo-default focus:outline-none focus:ring-[1.5px] focus:ring-kumo-focus/50"
                />
              ))}
            </OTPField.Root>

            {error && <Banner variant="error" description={error} />}

            <div className="flex items-center justify-between">
              <Button
                type="button"
                variant="ghost"
                disabled={cooldown > 0 || submitting}
                onClick={() => void sendCode(email.trim())}
              >
                {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setStep("email");
                  setCode("");
                  setError(null);
                }}
              >
                Use a different email
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
